import { FC, useCallback, useState } from 'react';
import classNames from 'classnames';

import { getRequestWithAccessToken } from '@rikstv/play-common/src/forces/utils/request';
import { useAuthToken } from '@rikstv/play-common/src/hooks/useAuthToken';
import {
  animateBounceClass,
  ThumbsDownFilledSvg,
  ThumbsDownSvg,
  ThumbsUpFilledSvg,
  ThumbsUpSvg,
} from '@rikstv/play-common/src/icons';
import { commonGoogleTagManager } from '@rikstv/play-common/src/utils/analytics/googleTagManager';
import { authService } from '@rikstv/play-common/src/utils/auth/AuthService';
import { RequiredProps } from '@rikstv/play-common/src/utils/types/typeUtils';
import { TertiaryIconButton } from '@rikstv/shared-components';

import { ContentStackMetadata } from '../../contentStack.types';

import styles from './RatingButtons.module.css';

type RatingAction = Parameters<typeof commonGoogleTagManager.rateFilmOrSeries>[0];

export const RatingButtons: FC<{ data: ContentStackMetadata }> = ({ data }) => {
  const { sentiment, likeOrDislike, animate } = useRatingButtons(data);

  if (!authService.isAuthenticated() || hasRatingLinks(data._links) === false) {
    return null;
  }

  return (
    <>
      <TertiaryIconButton
        title="Gi meg mer som dette i anbefalinger"
        className={styles.button}
        onClick={() => likeOrDislike('liked')}
        icon={() =>
          sentiment === 'liked' ? (
            <ThumbsUpFilledSvg aria-label="Tommel opp" className={classNames({ [animateBounceClass]: animate })} />
          ) : (
            <ThumbsUpSvg aria-label="Tommel opp" />
          )
        }
      />
      <TertiaryIconButton
        title="Gi meg mindre som dette i anbefalinger"
        className={styles.button}
        onClick={() => likeOrDislike('disliked')}
        icon={() =>
          sentiment === 'disliked' ? (
            <ThumbsDownFilledSvg aria-label="Tommel ned" className={classNames({ [animateBounceClass]: animate })} />
          ) : (
            <ThumbsDownSvg aria-label="Tommel ned" />
          )
        }
      />
    </>
  );
};

const useRatingButtons = (data: ContentStackMetadata) => {
  const request = useAuthenticatedRequest();
  const [loading, setIsLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [sentiment, setSentiment] = useState<'liked' | 'disliked' | null>(() => {
    if (data.userData?.thumbRating === 'Dislike') {
      return 'disliked';
    } else if (data.userData?.thumbRating === 'Like') {
      return 'liked';
    }
    return null;
  });

  const likeOrDislike = async (_sentiment: 'liked' | 'disliked') => {
    if (loading) {
      // Early return if request is already in progress
      // could have disabled buttons but the styling causes flashing that is kinda irritating
      // so wen t for this solution instead to avoid rage clicks etc
      return;
    }
    const lastValue = sentiment;
    const unset = _sentiment === sentiment;
    const method = unset ? 'DELETE' : 'POST';
    let action: RatingAction = _sentiment === 'liked' ? 'like' : 'dislike';
    if (unset) {
      action = `remove-${action}`;
    }

    if (hasRatingLinks(data._links)) {
      try {
        commonGoogleTagManager.rateFilmOrSeries(action, data.metadata.title);
        setSentiment(s => (s === _sentiment ? null : _sentiment));
        await request(_sentiment === 'liked' ? data._links.like.href : data._links.dislike.href, { method });
      } catch (err) {
        // reset value since request failed
        setSentiment(lastValue);
      } finally {
        setIsLoading(false);
        setAnimate(true);
      }
    }
  };

  return {
    sentiment,
    likeOrDislike,
    animate,
  };
};

const hasRatingLinks = (
  _links: ContentStackMetadata['_links']
): _links is RequiredProps<ContentStackMetadata['_links'], 'like' | 'dislike'> => {
  return _links.like?.href != null && _links.dislike?.href != null;
};

const useAuthenticatedRequest = () => {
  const token = useAuthToken();
  type Params = Parameters<ReturnType<typeof getRequestWithAccessToken>>;
  return useCallback((...args: Params) => getRequestWithAccessToken(token)(...args), [token]);
};
